/** Canonical app origin for absolute URLs (emails, impersonation). Falls back to current page origin. */
export const getAppOrigin = () =>
  (process.env.REACT_APP_APP_URL || "").replace(/\/$/, "") ||
  (typeof window !== "undefined" ? window.location.origin.replace(/\/$/, "") : "");

const MAIN_MARKETING_HOSTS = new Set([
  "fractolabs.com",
  "www.fractolabs.com",
  "localhost",
  "127.0.0.1",
]);

const SETTRHQ_HOSTS = new Set([
  "settrhq.com",
  "www.settrhq.com",
]);

const FRACTOLABS_APEX = "fractolabs.com";

/** Legacy subdomain → root landing key for `/`. */
export const SUBDOMAIN_ROOT_LANDING = {
  partner: "partner",
  roofing: "roofing",
  inbnd: "realEstate",
  ai: "loginRedirect",
};

export function getHostname() {
  return typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
}

export function isMainMarketingHost(hostname = getHostname()) {
  return MAIN_MARKETING_HOSTS.has(hostname);
}

export function isSettrHqHost(hostname = getHostname()) {
  return SETTRHQ_HOSTS.has(hostname.toLowerCase());
}

const DEFAULT_SITE_BRAND = {
  name: "FractoLabs",
  legalName: "FractoLabs Pty Ltd",
  logoSrc: "/images/logo.png",
};

const SETTRHQ_SITE_BRAND = {
  name: "SettrHQ",
  legalName: "SettrHQ Pty Ltd",
  logoSrc: "/images/logo_settrhq.png",
};

/** Single place that picks the brand name, legal name, and logo for the current host. */
export function getSiteBrand(hostname = getHostname()) {
  if (isSettrHqHost(hostname)) return SETTRHQ_SITE_BRAND;
  return DEFAULT_SITE_BRAND;
}

/** Single place that picks the logo for the current host. */
export function getSiteLogo(hostname = getHostname()) {
  const brand = getSiteBrand(hostname);
  return { src: brand.logoSrc, alt: brand.name };
}

const LIGHT_LANDING_LOGOS = {
  fractolabs: {
    src: "/images/logo_fractolabs_dark_gray.png",
    alt: "FractoLabs",
  },
  settrhq: {
    src: "/images/logo_settrhq_dark_gray.png",
    alt: "SettrHQ",
  },
};

/** Dark-gray header mark for the light roofing landings. FractoLabs unless the host is SettrHQ. */
export function getLightLandingLogo(hostname = getHostname()) {
  if (isSettrHqHost(hostname)) return LIGHT_LANDING_LOGOS.settrhq;
  return LIGHT_LANDING_LOGOS.fractolabs;
}

const RESEARCH_LAB_PHRASE = "FRACTOLABS RESEARCH LAB";
const RESEARCH_LAB_TOKEN = "\u0000FRACTOLABS_RESEARCH_LAB\u0000";

function replaceLandingBrandText(text) {
  return text
    .replaceAll(RESEARCH_LAB_PHRASE, RESEARCH_LAB_TOKEN)
    .replaceAll("FractoLabs", "SettrHQ")
    .replaceAll(RESEARCH_LAB_TOKEN, RESEARCH_LAB_PHRASE);
}

/** On settrhq.com, swap the FractoLabs name in landing copy. The research-lab line stays. */
export function applyLandingBrand(value, hostname = getHostname()) {
  if (!isSettrHqHost(hostname)) return value;
  if (typeof value === "string") return replaceLandingBrandText(value);
  if (Array.isArray(value)) return value.map((item) => applyLandingBrand(item, hostname));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, applyLandingBrand(item, hostname)]),
    );
  }
  return value;
}

/** Returns subdomain label for *.fractolabs.com, or null for apex/www/non-fractolabs hosts. */
export function getFractoLabsSubdomain(hostname = getHostname()) {
  const normalized = hostname.toLowerCase();
  if (normalized === FRACTOLABS_APEX || normalized === `www.${FRACTOLABS_APEX}`) {
    return null;
  }
  if (!normalized.endsWith(`.${FRACTOLABS_APEX}`)) {
    return null;
  }
  const subdomain = normalized.slice(0, -(FRACTOLABS_APEX.length + 1));
  if (!subdomain || subdomain.includes(".")) {
    return null;
  }
  return subdomain;
}

export function hasLandingUnlockParam(search = "") {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(normalized).has("l");
}

/**
 * Resolves which root landing to show at `/`.
 * @returns {"main"|"settrhq"|"partner"|"roofing"|"realEstate"|"loginRedirect"|"stub"}
 */
export function resolveRootLandingKey(
  hostname = getHostname(),
  search = typeof window !== "undefined" ? window.location.search : "",
  storedLeadId = null,
) {
  if (isSettrHqHost(hostname)) {
    return "settrhq";
  }

  if (isMainMarketingHost(hostname)) {
    return "main";
  }

  const subdomain = getFractoLabsSubdomain(hostname);
  let key;
  if (subdomain === null) {
    key = "stub";
  } else {
    key = SUBDOMAIN_ROOT_LANDING[subdomain] ?? "stub";
  }

  if (key === "stub" && (hasLandingUnlockParam(search) || Boolean(storedLeadId))) {
    return "main";
  }
  return key;
}
